import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Disc3 } from "lucide-react";
import { RECORDING } from "@/data";
import { Lightbox, resolveSrc } from "@/components/Gallery";

const API = process.env.REACT_APP_BACKEND_URL;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 },
  }),
};

const Sprockets = () => (
  <div className="flex justify-between px-2 py-1.5" aria-hidden="true">
    {Array.from({ length: 7 }).map((_, i) => (
      <span key={i} className="h-2 w-3 rounded-[2px] bg-[#070707]" />
    ))}
  </div>
);

export default function Studio() {
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/gallery?collection=studio`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setPhotos(list.map((p) => ({ ...p, src: resolveSrc(p.src) }))))
      .catch(() => setPhotos([]));
  }, []);
  const n = photos.length;
  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i === null ? i : (i - 1 + n) % n)), [n]);
  const next = useCallback(() => setActive((i) => (i === null ? i : (i + 1) % n)), [n]);

  return (
    <section
      id="studio"
      data-testid="studio-section"
      className="relative overflow-hidden border-t border-neutral-900 bg-[#090909]"
    >
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[30rem] w-[30rem] bg-[radial-gradient(circle,rgba(200,16,46,0.14),transparent_65%)] blur-3xl" />
      <div className="mx-auto max-w-7xl px-5 pt-28 sm:px-8 sm:pt-36">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <motion.p
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="font-mono-tac text-xs uppercase tracking-[0.35em] text-red-600"
            >
              06 — In studio
            </motion.p>
            <motion.h2
              variants={reveal}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="font-display mt-4 text-5xl uppercase leading-none text-neutral-100 sm:text-7xl"
            >
              Backstage delle <span className="text-outline-red">registrazioni</span>
            </motion.h2>
          </div>
          <motion.p
            variants={reveal}
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-md text-base leading-relaxed text-neutral-400"
          >
            Cuffie, cavi, un teschio di guardia alla batteria e qualche birra.
            Così è nato l'EP, dentro al {RECORDING.studio}: sei brani, zero
            compromessi.
          </motion.p>
        </div>
      </div>

      <motion.div
        variants={reveal}
        custom={2}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        className="mt-14 -rotate-1 bg-[#141414] pb-10"
      >
        <Sprockets />
        <div
          data-testid="studio-filmstrip"
          className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-1 sm:px-8 [scrollbar-width:thin] [scrollbar-color:#C8102E_#141414]"
        >
          {photos.map((photo, i) => (
            <button
              key={photo.id}
              data-testid={`studio-photo-${i + 1}`}
              onClick={() => setActive(i)}
              aria-label={`Apri foto: ${photo.alt}`}
              className={`group relative h-[300px] shrink-0 snap-center overflow-hidden border border-black/60 text-left sm:h-[420px] ${
                photo.wide ? "w-[400px] sm:w-[560px]" : "w-[225px] sm:w-[315px]"
              }`}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                className="h-full w-full object-cover sepia-[0.35] contrast-[1.05] transition-all duration-700 ease-out group-hover:scale-105 group-hover:sepia-0"
              />
              <span className="absolute left-3 top-3 font-mono-tac text-[10px] tracking-[0.3em] text-neutral-200/80 mix-blend-difference">
                {String(i + 1).padStart(2, "0")}A
              </span>
              <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/85 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <Disc3 className="h-3.5 w-3.5 shrink-0 text-red-500" />
                <span className="font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-200">
                  {photo.alt}
                </span>
              </div>
            </button>
          ))}
          <div className="w-2 shrink-0" />
        </div>
        <Sprockets />
      </motion.div>

      <div className="mx-auto max-w-7xl px-5 pb-28 sm:px-8 sm:pb-36">
        <motion.dl
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          data-testid="recording-credits"
          className="-mt-6 grid grid-cols-1 gap-px border border-neutral-800 bg-neutral-800 sm:grid-cols-2 lg:grid-cols-4"
        >
          {[
            { k: "Registrato", v: RECORDING.period, sub: `${RECORDING.studio} — ${RECORDING.studioCity}` },
            { k: "Mix & Mastering", v: RECORDING.mixStudio, sub: RECORDING.mixCity },
            { k: "Ingegnere del suono", v: RECORDING.engineer, sub: "Ripresa, mix e master" },
            { k: "Prodotto da", v: RECORDING.label, sub: "℗ 2026 — Linea Cremisi EP" },
          ].map((c) => (
            <div key={c.k} className="bg-[#0D0D0D] p-6">
              <dt className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-red-600">
                {c.k}
              </dt>
              <dd className="font-serif-logo mt-3 text-base font-bold tracking-wide text-neutral-100">
                {c.v}
              </dd>
              <dd className="mt-1 text-sm text-neutral-500">{c.sub}</dd>
            </div>
          ))}
        </motion.dl>
      </div>

      <AnimatePresence>
        {active !== null && (
          <Lightbox
            photos={photos}
            index={active}
            onClose={close}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
